import React, { useEffect, useState } from "react";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Calendar as CalIcon, Clock, MapPin, Target, Award, Activity as ActivityIcon, Sparkles, Trophy } from "lucide-react";
import Avatar from "../components/app/Avatar";
import CountUp from "../components/app/CountUp";

/**
 * Player dashboard — for users with role=player.
 * For MVP, the player sees:
 *  - their welcome hero
 *  - next training countdown
 *  - linked profile stats (if a coach has linked them to a child via children.player_user_id)
 *  - upcoming matches
 *  - basic activity feed
 */
export default function PlayerDashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [trainings, setTrainings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [linked, setLinked] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [tr, m] = await Promise.all([api.get("/trainings"), api.get("/matches")]);
        setTrainings(tr.data);
        setMatches(m.data);
      } catch (_) {}
    })();
  }, []);

  const upcoming = trainings.filter((tr) => new Date(tr.start_at) > new Date()).slice(0, 5);
  const next = upcoming[0];

  // Live countdown
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = next ? Math.max(0, new Date(next.start_at).getTime() - now) : 0;
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);

  const stat = linked || {};

  return (
    <Layout>
      <div className="space-y-6" data-testid="player-dashboard-root">
        {/* Hero */}
        <div className="relative card-sharp overflow-hidden grain animate-fade-up" style={{ opacity: 0 }}>
          <div className="spotlight" />
          <div className="absolute inset-0 pitch-field opacity-40" />
          <div className="relative p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start">
            <Avatar name={user?.name} src={user?.photo_url} size="xl" ring />
            <div className="flex-1">
              <div className="label-mono text-pitch mb-2">// PLAYER COMMAND CENTER</div>
              <h1 className="font-oswald uppercase text-4xl sm:text-5xl tracking-tight leading-none">
                {t("welcome")},<br />
                <span className="text-pitch">{user?.name?.split(" ")[0]}</span>
              </h1>
              <p className="text-white/50 mt-3 max-w-xl text-sm">
                {lang === "bg" ? "Тренирай, играй, прогресирай." : "Train. Play. Progress."}
              </p>
            </div>
          </div>
        </div>

        {/* Countdown to next training (always rendered with empty state) */}
        <div className="card-sharp p-6 relative overflow-hidden animate-fade-up" style={{ animationDelay: "100ms", opacity: 0 }} data-testid="countdown-card">
          <div className="absolute inset-0 pitch-field opacity-30" />
          <div className="relative">
            <div className="label-mono mb-2 text-pitch flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> // {lang === "bg" ? "До следваща тренировка" : "Next training in"}
            </div>
            {next ? (
              <>
                <div className="font-oswald uppercase text-3xl mb-3 tracking-tight">{next.title}</div>
                <div className="grid grid-cols-4 gap-3 max-w-xl">
                  {[
                    [days, lang === "bg" ? "дни" : "days"],
                    [hours, "h"],
                    [mins, "m"],
                    [secs, "s"],
                  ].map(([n, lab], i) => (
                    <div key={i} className="border border-white/5 p-3 text-center">
                      <div className="font-oswald text-5xl text-pitch tracking-tighter">{String(n).padStart(2, "0")}</div>
                      <div className="label-mono mt-1">{lab}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 mt-3 text-white/50 text-sm">
                  <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-pitch" />{new Date(next.start_at).toLocaleString(lang === "bg" ? "bg-BG" : "en-US")}</span>
                  {next.location && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-pitch" />{next.location}</span>}
                </div>
              </>
            ) : (
              <>
                <div className="font-oswald uppercase text-2xl mb-3 tracking-tight text-white/40">
                  {lang === "bg" ? "Няма предстоящи тренировки" : "No upcoming training"}
                </div>
                <div className="grid grid-cols-4 gap-3 max-w-xl">
                  {(lang === "bg" ? ["дни", "h", "m", "s"] : ["days", "h", "m", "s"]).map((lab, i) => (
                    <div key={i} className="border border-white/5 p-3 text-center">
                      <div className="font-oswald text-5xl text-white/20 tracking-tighter">00</div>
                      <div className="label-mono mt-1">{lab}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-white/40">
                  {lang === "bg" ? "Свържи се с треньора си." : "Ask your coach to schedule one."}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Performance card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Stat label={lang === "bg" ? "Голове" : "Goals"} value={stat.goals ?? 0} icon={Target} />
          <Stat label={lang === "bg" ? "Асистенции" : "Assists"} value={stat.assists ?? 0} icon={Award} />
          <Stat label={lang === "bg" ? "Мачове" : "Matches"} value={stat.matches_played ?? 0} icon={ActivityIcon} />
        </div>

        {/* Upcoming */}
        <div className="card-sharp p-6 animate-fade-up" style={{ animationDelay: "150ms", opacity: 0 }}>
          <h2 className="font-oswald uppercase text-2xl tracking-tight mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-pitch" /> {lang === "bg" ? "Предстоящи мачове" : "Upcoming matches"}
          </h2>
          {matches.filter((m) => new Date(m.start_at) > new Date()).length === 0 ? (
            <div className="text-white/30 py-8 text-center font-mono text-xs tracking-widest">{t("noMatches")}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {matches.filter((m) => new Date(m.start_at) > new Date()).slice(0, 4).map((m) => (
                <div key={m.id} className="border border-white/5 p-4">
                  <div className="label-mono mb-1">{m.is_home ? t("home") : t("away")}</div>
                  <div className="font-oswald uppercase text-lg tracking-tight">{t("vs")} {m.opponent}</div>
                  <div className="text-xs text-white/40 mt-1">{new Date(m.start_at).toLocaleDateString(lang === "bg" ? "bg-BG" : "en-US")}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-sharp p-5 text-center text-white/45 text-sm animate-fade-up flex items-center justify-between gap-4 flex-wrap" style={{ animationDelay: "200ms", opacity: 0 }}>
          <div className="text-left">
            <div className="label-mono mb-1 text-pitch">// {lang === "bg" ? "Свържи се с треньор" : "Connect with a coach"}</div>
            <div className="text-white/65 text-sm">
              {lang === "bg"
                ? "Намери своя личен треньор и започни да следиш прогреса си."
                : "Find your private coach and start tracking your progress."}
            </div>
          </div>
          <Link to="/find-coaches" className="btn-primary px-5 py-2.5 text-xs whitespace-nowrap">
            {lang === "bg" ? "Намери треньор" : "Find a coach"}
          </Link>
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="card-sharp stat-card p-5 relative overflow-hidden animate-fade-up">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mono mb-2">{label}</div>
          <div className="font-oswald text-4xl tracking-tight text-pitch leading-none">
            <CountUp value={value} />
          </div>
        </div>
        <Icon className="w-5 h-5 text-pitch" />
      </div>
    </div>
  );
}
